import { lazy, Suspense } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ApplicantProvider } from './contexts/ApplicantContext';
import { AutofillExportPage } from './pages/AutofillExport';
import { firebaseAppReady } from './firebase';
import { SignInPage, SignUpPage } from './pages/Auth';
import { AppLayout } from './components/AppLayout';

// Public marketing page — lazy so it never weighs down the authenticated bundle.
const Landing = lazy(() => import('./pages/Landing'));
const Privacy = lazy(() => import('./pages/Privacy'));
import { DashboardPage } from './pages/Dashboard';
import { ScholarshipsPage } from './pages/Scholarships';
import { ScholarshipDetailPage } from './pages/ScholarshipDetail';
import { InboxPage } from './pages/Inbox';
import { ProfilePage } from './pages/Profile';
import { DocumentsPage } from './pages/Documents';
import { SchoolsPage } from './pages/Schools';
import { PlannerPage } from './pages/Planner';
import { HouseholdPage } from './pages/Household';
import { CapturePage } from './pages/Capture';
import { WelcomePage } from './pages/Welcome';
import type { ReactNode } from 'react';

function ProtectedRoute({ children }: { children: ReactNode }) {
  const { user, loading, membership } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-cream">
        <div className="text-soft text-sm">Loading…</div>
      </div>
    );
  }
  if (!user) {
    return <Navigate to="/signin" replace />;
  }
  // Signed in but no household yet (a code-less signup that hasn't completed
  // first-run setup) -> send to /welcome. This is safe because `loading` stays
  // true until the FIRST membership lookup completes, so an existing member is
  // never briefly seen as membership-less. Only a genuinely household-less
  // user reaches this branch.
  if (!membership) {
    return <Navigate to="/welcome" replace />;
  }
  return <ApplicantProvider>{children}</ApplicantProvider>;
}

// Root route. This is the parent element for the whole authenticated route
// tree, so it renders for "/" AND every child path (/scholarships, /capture, …).
//
// - Logged-out visitor at exactly "/" → the public marketing landing page.
// - Logged-out visitor on any other path → falls through to ProtectedRoute,
//   which redirects to /signin exactly as before (so the extension's /capture
//   and all deep links keep working — they are NOT shown the landing page).
// - Signed-in user (any path) → the authenticated app, unchanged.
//
// `loading` is honored first so an existing member never flashes the landing
// page on a hard refresh.
function HomeRoute() {
  const { user, loading } = useAuth();
  const { pathname } = useLocation();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-cream">
        <div className="text-soft text-sm">Loading…</div>
      </div>
    );
  }
  if (!user && pathname === '/') {
    return (
      <Suspense fallback={<div className="min-h-screen bg-paper" />}>
        <Landing />
      </Suspense>
    );
  }
  return (
    <ProtectedRoute>
      <AppLayout />
    </ProtectedRoute>
  );
}

// Guard for the /welcome route itself: it requires a signed-in user, but must
// NOT require a membership (the whole point is the user has none yet). If the
// user already has a household, bounce them to the dashboard so first-run
// setup can't be re-run.
function WelcomeRoute() {
  const { user, loading, membership } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-cream">
        <div className="text-soft text-sm">Loading…</div>
      </div>
    );
  }
  if (!user) {
    return <Navigate to="/signin" replace />;
  }
  if (membership) {
    return <Navigate to="/" replace />;
  }
  return <WelcomePage />;
}

function FirebaseConfigMissing() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-cream px-4">
      <div className="max-w-md p-6 rounded-lg border border-rose-200 bg-rose-50 text-rose-900">
        <h1 className="text-lg font-semibold mb-2">Firebase not configured</h1>
        <p className="text-sm">
          The Firebase environment variables are missing. The predeploy script should sync these
          from your config path before build. Check that <code>SCHOLARSHIP_CONFIG_PATH</code> is set
          and contains a valid <code>.env.local</code> file.
        </p>
      </div>
    </div>
  );
}

function App() {
  if (!firebaseAppReady) {
    return <FirebaseConfigMissing />;
  }
  return (
    <BrowserRouter>
      <AuthProvider>
        <Routes>
          <Route path="/signin" element={<SignInPage />} />
          <Route path="/signup" element={<SignUpPage />} />
          <Route
            path="/privacy"
            element={
              <Suspense fallback={<div className="min-h-screen bg-cream" />}>
                <Privacy />
              </Suspense>
            }
          />
          <Route path="/welcome" element={<WelcomeRoute />} />
          <Route path="/autofill-export" element={
            <ProtectedRoute><AutofillExportPage /></ProtectedRoute>
          } />
          <Route path="/" element={<HomeRoute />}>
            <Route index element={<DashboardPage />} />
            <Route path="scholarships" element={<ScholarshipsPage />} />
            <Route path="scholarships/:id" element={<ScholarshipDetailPage />} />
            <Route path="inbox" element={<InboxPage />} />
            <Route path="schools" element={<SchoolsPage />} />
            <Route path="planner" element={<PlannerPage />} />
            <Route path="profile" element={<ProfilePage />} />
            <Route path="documents" element={<DocumentsPage />} />
            <Route path="household" element={<HouseholdPage />} />
            <Route path="capture" element={<CapturePage />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Route>
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
